import { Injectable } from '@angular/core';
import { ProspectLight } from '@lsq-frontend/remote-services/ar-app';
import { ProspectService } from '@lsq-frontend/remote-services/ar-app';
import { BehaviorSubject } from 'rxjs';
import ProspectTableMock from './admin-prospects.component.data.json';
import { DrApplicationService } from '@lsqlabs/dr-application-shared';
import { orderBy } from 'lodash';

@Injectable()
export class AdminProspectsService {
    public static defaultTake = 25;
    public prospectsRaw: ProspectLight[] = [];
    public prospects = new BehaviorSubject<ProspectLight[]>([]);
    public loading = new BehaviorSubject<boolean>(false);
    public totalCount = new BehaviorSubject<number>(0);
    public skip = new BehaviorSubject<number>(0);
    public take = new BehaviorSubject<number>(AdminProspectsService.defaultTake);
    public filterStatus = new BehaviorSubject<string>('');
    public filterSearch = new BehaviorSubject<string>('');
    public filterType = new BehaviorSubject<string>('');
    public filterInvitedBy = new BehaviorSubject<string>('');
    public filterSortColumn = new BehaviorSubject<string>('');
    public filterSortDirection = new BehaviorSubject<any>('');

    constructor(
        private _prospectService: ProspectService,
        private _arService: DrApplicationService
    ) {}

    public async loadProspects(
        hardReload: boolean = false,
        schemaTypes: Array<string> = []
    ): Promise<void> {
        this.loading.next(true);
        let localProspects: ProspectLight[] = this.prospectsRaw;

        if (hardReload || localProspects.length === 0) {
            const getProspect = this._prospectService.getProspectDataLight(
                null,
                null,
                null,
                schemaTypes,
                null
            );
            const prospects = await getProspect.toPromise();
            localProspects = prospects;
        }
        this.prospectsRaw = localProspects;
        this.totalCount.next(this.prospectsRaw?.length);
        this.prospects.next(localProspects.slice(0, this.take.value));
        this.loading.next(false);
    }

    public getMockProspects(): ProspectLight[] {
        const getProspects = () =>
            ProspectTableMock.map((pMock: any) => ({
                id: pMock.id,
                businessName: pMock.businessName,
                status: pMock.status as ProspectLight.StatusEnum,
                createdDate: new Date(pMock.createdDate),
                updatedDate: new Date(pMock.updatedDate)
            }));
        return [...getProspects(), ...getProspects()];
    }

    public handleTakeChange(take: number): void {
        this.filterProspects(take);
        this.take.next(take);
    }

    public handleSkipChange(skip: number): void {
        const take = this.take.value;
        this.filterProspects(take, skip);
        this.skip.next(skip);
    }

    public setFilters(searchText: string, status: string, type: string, invitedBy: string): void {
        this.filterStatus.next(status);
        this.filterSearch.next(searchText);
        this.filterType.next(type);
        this.filterInvitedBy.next(invitedBy);
    }

    public filterProspects(
        _take: number = this.take.value,
        _skip: number = this.skip.value,
        _hardFilter: boolean = false
    ): void {
        const skip = _hardFilter ? 0 : _skip;
        const take = _take;
        const searchText = this.filterSearch.value;
        const status = this.filterStatus.value;
        const type = this.filterType.value;
        const invitedBy = this.filterInvitedBy.value;

        const sortColumn = this.filterSortColumn.value;
        const sortDirection = this.filterSortDirection.value;

        let prospects = this.prospectsRaw;

        if (!!sortColumn) {
            prospects = orderBy(
                this.prospectsRaw,
                [(prospect: ProspectLight) => prospect[sortColumn]?.toLowerCase()],
                [OrderSortOption[sortDirection]]
            );
        }

        const newProspects = prospects.filter((prospect: ProspectLight) => {
            if (status && prospect.status !== status) {
                return false;
            }
            if (type && prospect.schemaType !== type) {
                return false;
            }
            if (invitedBy && prospect.coordinatorUserId !== invitedBy) {
                return false;
            }
            if (searchText) {
                return prospect.businessName.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
            }
            return true;
        });
        this.prospects.next(newProspects.slice(skip, skip + take));
        this.totalCount.next(newProspects.length);
    }

    public setSort(sortColumn: string, sortDirection: any): void {
        this.filterSortColumn.next(sortColumn);
        this.filterSortDirection.next(sortDirection);
    }

    public handleSortChange(): void {
        this.filterProspects(undefined, 0);
    }
}

export const OrderSortOption = {
    '1': 'asc',
    '-1': 'desc'
};
